import { createSlice } from '@reduxjs/toolkit';

import { ScenarioRTKService } from '../app/services/scenario-rtk.service';
import { RootState } from '../app/store';
import IScenariosState from '../interfaces/models/scenarios-state.interface';

const initialState: IScenariosState = {
  allScenarios: null,
  currentScenarioId: null,
  undoRedoSteps: [],
  currentStepIndex: -1,
  currentScenarioTitle: '',
};

const undoRedoMaxLength = 10;

const ScenariosSlice: any = createSlice({
  name: 'ScenariosSlice',
  initialState,
  reducers: {
    resetScenarios: () => initialState,
    resetUndoRedoScenario: (state, action) => {
      state.undoRedoSteps = [];
      state.currentStepIndex = -1;
    },
    setCurrentScenario: (state, action) => {
      if (action && action?.payload) {
        state.currentScenarioId = action.payload;
      }
    },
    setCurrentScenarioTitle: (state, action) => {
      if (action && action?.payload) {
        state.currentScenarioTitle = action.payload;
      }
    },
    addUndoRedoStep: (state, action) => {
      if (action.payload.step) {
        if (state.undoRedoSteps.length >= undoRedoMaxLength) {
          state.undoRedoSteps.shift();
          state.undoRedoSteps = [...state.undoRedoSteps, { ...action.payload.step }];
        } else {
          const steps = state.undoRedoSteps.slice(0, state.currentStepIndex + 1);
          state.undoRedoSteps = [...steps, { ...action.payload.step }];
          state.currentStepIndex++;
        }
      }
    },
    redoStep: state => {
      if (state.currentStepIndex < state.undoRedoSteps.length - 1) {
        state.currentStepIndex++;

        switch (state.undoRedoSteps[state.currentStepIndex].action) {
          case 'add':
            state.undoRedoSteps[state.currentStepIndex].action = 'remove';
            break;
          case 'remove':
            state.undoRedoSteps[state.currentStepIndex].action = 'add';
            break;

          default:
            break;
        }
      }
    },
    undoStep: state => {
      if (state.currentStepIndex > -1) {
        switch (state.undoRedoSteps[state.currentStepIndex].action) {
          case 'add':
            state.undoRedoSteps[state.currentStepIndex].action = 'remove';
            break;
          case 'remove':
            state.undoRedoSteps[state.currentStepIndex].action = 'add';
            break;

          default:
            break;
        }

        state.currentStepIndex--;
      }
    },
    replaceNewIdInSteps: (state, action) => {
      state.undoRedoSteps = state.undoRedoSteps.map(step => {
        const _step = { ...step };

        if (_step?.data?.id == action?.payload?.exId) _step.data.id = action?.payload?.newId;

        if (_step?.data?.parent && _step?.data?.parent == action?.payload?.exId) _step.data.parent = action?.payload?.newId;

        if (_step?.data?.next && _step?.data?.next == action?.payload?.exId) _step.data.next = action?.payload?.newId;

        return _step;
      });
    },
    updateChangedStep: (state, action) => {
      const index = action.payload.type === 'redo' ? state.currentStepIndex + 1 : state.currentStepIndex;
      state.undoRedoSteps[index] = {
        ...state.undoRedoSteps[index],
        data: action.payload.step,
      };
    },
  },
  extraReducers: builder => {
    builder.addMatcher(ScenarioRTKService.endpoints.getAllScenarios.matchFulfilled, (state, action) => {
      state.allScenarios = action.payload;
    });
  },
});

export const {
  setCurrentScenario,
  setCurrentScenarioTitle,
  addUndoRedoStep,
  redoStep,
  undoStep,
  replaceNewIdInSteps,
  updateChangedStep,
  resetUndoRedoScenario,
} = ScenariosSlice.actions;

export const selectUndoRedoSteps = (state: RootState) => (state.scenarioState as IScenariosState)?.undoRedoSteps;

export const selectCurrentStepIndex = (state: RootState) => (state.scenarioState as IScenariosState)?.currentStepIndex;

export const selectCurrentScenarioTitle = (state: RootState) => (state.scenarioState as IScenariosState)?.currentScenarioTitle;

export default ScenariosSlice.reducer;
